import { GridWall, Heading, Loader, Text, useIsTheme, Theme } from '@afterpaytouch/core'
import { CheckoutMode } from '@afterpaytouch/portal-api/types'
import clsx from 'clsx'
import { useTranslation } from '../../utils/convergence'
import React, { FunctionComponent } from 'react'
import { PostCheckoutFlow } from '../../model'
import { useCheckoutMode, useIsApplePaylater, useIsBillingAgreementApprovalProcess, useMerchantDisplayNameOrName, usePostCheckoutExperiment } from '../../state'
import { usePersonalization } from '../../utils/personalization'
import { getProcessingAssetTranslation } from '../../utils/translation-helpers'

import styles from './style.module.css'
import { useIsSupFlow } from './ProcessOrder.hooks'

interface ProcessingText {
  heading: string
  description: string
  instruction: string
}

type ProcessingTextType = keyof ProcessingText

export const ProcessOrder: FunctionComponent = () => {
  const { t } = useTranslation()
  const checkoutMode = useCheckoutMode()
  const merchantName = useMerchantDisplayNameOrName()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const isApplePaylater = useIsApplePaylater()
  const {
    postCheckout: {
      asset: { id: assetId },
      flow,
      showComponentState: { showComponent: showPostCheckout },
    },
  } = usePersonalization()
  const { value: postCheckoutExperimentValue } = usePostCheckoutExperiment()
  const isPreCapture = flow === PostCheckoutFlow.PreCapture
  const isPreCaptureFlow = isPreCapture && showPostCheckout
  // Add margin for headings without the loader
  const headingStyle = clsx(styles.contentWrapper, isPreCaptureFlow && styles.marginTop)
  const isSupFlow = useIsSupFlow()
  const isPegasusTheme = useIsTheme(Theme.Pegasus)
  const isKelpieTheme = useIsTheme(Theme.Kelpie)
  const isKelpieMintTheme = useIsTheme(Theme.KelpieMint)
  const isPegasusOrKelpieTheme = isPegasusTheme || isKelpieTheme || isKelpieMintTheme

  const fallbackTranslationOptions = {
    defaultValue: '',
  }

  const descriptionTranslationOptions = {
    ...fallbackTranslationOptions,
    merchantName,
  }

  const getTranslationOptionsByType = (type: ProcessingTextType) => {
    if (type === 'description') {
      return descriptionTranslationOptions
    }

    return fallbackTranslationOptions
  }

  const getPreCaptureText = (type: ProcessingTextType): string => {
    return getProcessingAssetTranslation({
      id: assetId,
      variant: postCheckoutExperimentValue,
      flow,
      type,
      t,
      options: getTranslationOptionsByType(type),
    })
  }

  const getDefaultDescription = (): string => {
    if (checkoutMode === CheckoutMode.EXPRESS) {
      return t('processing:description_express', descriptionTranslationOptions)
    }

    return t('processing:description', descriptionTranslationOptions)
  }

  const getNonPreCaptureText = (): ProcessingText => {
    if (isApplePaylater) {
      return {
        heading: t('processing:applePaylater.heading', fallbackTranslationOptions),
        description: '',
        instruction: '',
      }
    }

    if (isBillingAgreementApprovalProcess) {
      return {
        heading: t('processing:billingAgreementApproval.heading', fallbackTranslationOptions),
        description: t('processing:billingAgreementApproval.description', descriptionTranslationOptions),
        instruction: '',
      }
    }

    if (isSupFlow) {
      return {
        heading: t('processing:supOrder.heading', fallbackTranslationOptions),
        description: t('processing:supOrder.description', descriptionTranslationOptions),
        instruction: '',
      }
    }

    return {
      heading: t('processing:heading', fallbackTranslationOptions),
      description: getDefaultDescription(),
      instruction: t('processing:instruction', fallbackTranslationOptions),
    }
  }

  const getProcessingText = (): ProcessingText => {
    if (isPreCaptureFlow) {
      return {
        heading: getPreCaptureText('heading'),
        description: getPreCaptureText('description'),
        instruction: getPreCaptureText('instruction'),
      }
    }

    return getNonPreCaptureText()
  }

  const { heading, description, instruction } = getProcessingText()

  return (
    <>
      {!isPreCaptureFlow && <Loader testNameSpace='processing-order' />}
      <div className={headingStyle}>
        <Heading testNameSpace='process-order-heading' size={isPegasusOrKelpieTheme ? 'S' : 'XL'} color={isPegasusOrKelpieTheme ? 'Prominent' : undefined}>
          {heading}
        </Heading>
      </div>
      {(description || instruction) && (
        <div className={styles.contentWrapper}>
          <GridWall columns={1} gutter={'small'}>
            {description && (
              <Text testNameSpace='process-order-description' size='S' color={isPegasusOrKelpieTheme ? 'Subtle' : undefined}>
                {description}
              </Text>
            )}
            {instruction && (
              <Text testNameSpace='process-order-instruction' size='S' color={isPegasusOrKelpieTheme ? 'Prominent' : undefined} bold>
                {instruction}
              </Text>
            )}
          </GridWall>
        </div>
      )}
    </>
  )
}
